import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const VIEWBOX_W = 800;
const VIEWBOX_H = 500;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 60 };
const TOP_RATIO = 0.62;
const GAP = 30;

const TOP_HEIGHT = Math.round((VIEWBOX_H - GAP) * TOP_RATIO);
const BOTTOM_HEIGHT = VIEWBOX_H - TOP_HEIGHT - GAP;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: { fontSize: '16px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' },
  subtitle: { fontSize: '12px', color: '#9ca3af', marginBottom: '20px' },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '14px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  infoBox: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '16px',
    fontSize: '12px',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxHeight: '320px',
    overflowY: 'auto',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

export default function PricePathDivergenceChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Validate data
  const safeData = useMemo(() => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
  const emptyStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '320px',
    background: '#f9fafb',
    border: '1px dashed #e5e7eb',
    borderRadius: '8px',
    color: '#6b7280',
    fontSize: '14px',
    fontStyle: 'italic'
  };

  return <div style={emptyStyle}>No analytical data available for this selection.</div>;
}
    if (!data.paths || data.paths.length === 0) return null;
    if (!data.divergenceIndex || data.divergenceIndex.length === 0) return null;
    // Ensure at least one path has normalizedPath entries
    const hasPathData = data.paths.some(
      (p) => p.normalizedPath && p.normalizedPath.length > 0
    );
    if (!hasPathData) return null;
    return data;
  }, [data]);

  // Unique states from paths
  const uniqueStates = useMemo(() => {
    if (!safeData) return [];
    const stateSet = new Set();
    safeData.paths.forEach((p) => {
      if (p.state) stateSet.add(p.state);
    });
    return Array.from(stateSet).sort();
  }, [safeData]);

  // Stable color scale
  const stateColorScale = useMemo(() => {
    return d3.scaleOrdinal(d3.schemeTableau10).domain(uniqueStates);
  }, [uniqueStates]);

  // Compute the average path from all normalizedPath entries
  const averagePath = useMemo(() => {
    if (!safeData) return [];
    // Collect all unique dates across all paths
    const dateMap = new Map();
    safeData.paths.forEach((p) => {
      if (!p.normalizedPath) return;
      p.normalizedPath.forEach((pt) => {
        if (!dateMap.has(pt.date)) {
          dateMap.set(pt.date, { sum: 0, count: 0 });
        }
        const entry = dateMap.get(pt.date);
        entry.sum += pt.value;
        entry.count += 1;
      });
    });
    return Array.from(dateMap.entries())
      .map(([date, { sum, count }]) => ({ date, value: sum / count }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [safeData]);

  useEffect(() => {
    if (!safeData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('viewBox', `0 0 ${VIEWBOX_W} ${VIEWBOX_H}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const innerW = VIEWBOX_W - MARGIN.left - MARGIN.right;
    const topInnerH = TOP_HEIGHT - MARGIN.top - 10;
    const bottomInnerH = BOTTOM_HEIGHT - MARGIN.bottom - 10;

    // ──────────────────────────────────────────
    // Collect all dates for shared X scale
    // ──────────────────────────────────────────
    const allDatesSet = new Set();
    safeData.paths.forEach((p) => {
      if (p.normalizedPath) {
        p.normalizedPath.forEach((pt) => allDatesSet.add(pt.date));
      }
    });
    safeData.divergenceIndex.forEach((pt) => allDatesSet.add(pt.date));

    const allDates = Array.from(allDatesSet).sort();
    const parsedDates = allDates.map((d) => new Date(d));

    if (parsedDates.length === 0) return;

    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    // ──────────────────────────────────────────
    // TOP PANEL — Spaghetti plot of normalized price paths
    // ──────────────────────────────────────────
    const topG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Compute Y domain from all path values
    const allPathValues = [];
    safeData.paths.forEach((p) => {
      if (p.normalizedPath) {
        p.normalizedPath.forEach((pt) => allPathValues.push(pt.value));
      }
    });
    averagePath.forEach((pt) => allPathValues.push(pt.value));

    const yMin = allPathValues.length > 0 ? d3.min(allPathValues) : 0.9;
    const yMax = allPathValues.length > 0 ? d3.max(allPathValues) : 1.1;
    const yPadding = Math.max((yMax - yMin) * 0.12, 0.02);

    const yTop = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([topInnerH, 0]);

    // Grid lines
    topG.selectAll('.grid-line')
      .data(yTop.ticks(6))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yTop(d))
      .attr('y2', (d) => yTop(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Reference line at y = 1.0
    topG.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', yTop(1.0))
      .attr('y2', yTop(1.0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '6,4');

    topG.append('text')
      .attr('x', innerW + 4)
      .attr('y', yTop(1.0))
      .attr('dominant-baseline', 'central')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text('1.0');

    // Line generator for individual paths
    const pathLineGen = d3.line()
      .x((d) => x(new Date(d.date)))
      .y((d) => yTop(d.value))
      .defined((d) => d.value != null)
      .curve(d3.curveMonotoneX);

    // Draw all individual paths
    const mostDivAddr = safeData.mostDivergentAddress;

    safeData.paths.forEach((path) => {
      if (!path.normalizedPath || path.normalizedPath.length < 2) return;

      const isMostDivergent = path.addressId === mostDivAddr;
      const color = path.state ? stateColorScale(path.state) : '#9ca3af';

      topG.append('path')
        .datum(path.normalizedPath)
        .attr('d', pathLineGen)
        .attr('fill', 'none')
        .attr('stroke', isMostDivergent ? '#dc2626' : color)
        .attr('stroke-width', isMostDivergent ? 2 : 0.8)
        .attr('stroke-opacity', isMostDivergent ? 0.9 : 0.3);
    });

    // Average path (thick dashed)
    if (averagePath.length > 1) {
      topG.append('path')
        .datum(averagePath)
        .attr('d', pathLineGen)
        .attr('fill', 'none')
        .attr('stroke', '#1f2937')
        .attr('stroke-width', 2.5)
        .attr('stroke-dasharray', '8,4');
    }

    // Annotate most divergent address at the end of its path
    if (mostDivAddr) {
      const mostPath = safeData.paths.find((p) => p.addressId === mostDivAddr);
      if (mostPath && mostPath.normalizedPath && mostPath.normalizedPath.length > 0) {
        const lastPt = mostPath.normalizedPath[mostPath.normalizedPath.length - 1];
        const cx = x(new Date(lastPt.date));
        const cy = yTop(lastPt.value);
        topG.append('circle')
          .attr('cx', cx)
          .attr('cy', cy)
          .attr('r', 4)
          .attr('fill', '#dc2626')
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
      }
    }

    // Y axis (top)
    topG.append('g')
      .call(
        d3.axisLeft(yTop)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => v.toFixed(2))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label (top)
    topG.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -topInnerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Normalized Price');

    // Panel label
    topG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Normalized Price Paths');

    // Crosshair (top)
    const crosshairTop = topG.append('line')
      .attr('y1', 0)
      .attr('y2', topInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // ──────────────────────────────────────────
    // BOTTOM PANEL — Divergence index area chart
    // ──────────────────────────────────────────
    const bottomTopY = TOP_HEIGHT + GAP;
    const bottomG = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${bottomTopY})`);

    // Panel label
    bottomG.append('text')
      .attr('x', 0)
      .attr('y', -6)
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#374151')
      .text('Divergence Index');

    // Y scale
    const divValues = safeData.divergenceIndex.map((d) => d.value).filter((v) => v != null);
    const divMax = divValues.length > 0 ? d3.max(divValues) : 0.1;
    const divPadding = divMax * 0.15 || 0.01;

    const yBottom = d3.scaleLinear()
      .domain([0, divMax + divPadding])
      .nice()
      .range([bottomInnerH, 0]);

    // Grid lines
    bottomG.selectAll('.bottom-grid')
      .data(yBottom.ticks(4))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => yBottom(d))
      .attr('y2', (d) => yBottom(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Area fill
    const divPoints = safeData.divergenceIndex
      .filter((d) => d.value != null)
      .map((d) => ({ date: new Date(d.date), value: d.value }));

    if (divPoints.length > 1) {
      const areaGen = d3.area()
        .x((d) => x(d.date))
        .y0(yBottom(0))
        .y1((d) => yBottom(d.value))
        .curve(d3.curveMonotoneX);

      bottomG.append('path')
        .datum(divPoints)
        .attr('d', areaGen)
        .attr('fill', '#8b5cf6')
        .attr('fill-opacity', 0.15);

      const divLineGen = d3.line()
        .x((d) => x(d.date))
        .y((d) => yBottom(d.value))
        .curve(d3.curveMonotoneX);

      bottomG.append('path')
        .datum(divPoints)
        .attr('d', divLineGen)
        .attr('fill', 'none')
        .attr('stroke', '#8b5cf6')
        .attr('stroke-width', 2);
    }

    // Y axis (bottom)
    bottomG.append('g')
      .call(
        d3.axisLeft(yBottom)
          .ticks(4)
          .tickSize(0)
          .tickFormat((v) => v.toFixed(3))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis (bottom panel)
    bottomG.append('g')
      .attr('transform', `translate(0, ${bottomInnerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(allDates.length, 8))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    // Crosshair (bottom)
    const crosshairBottom = bottomG.append('line')
      .attr('y1', 0)
      .attr('y2', bottomInnerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Hover dot for divergence
    const divDot = bottomG.append('circle')
      .attr('r', 4)
      .attr('fill', '#8b5cf6')
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('display', 'none');

    // ──────────────────────────────────────────
    // HOVER — shared across both panels
    // ──────────────────────────────────────────
    function handleHover(event, panelG) {
      const [mx] = d3.pointer(event, panelG.node());
      const dateAtMouse = x.invert(mx);

      // Find closest date
      const timestamps = parsedDates.map((d) => d.getTime());
      const bisect = d3.bisector((d) => d).left;
      let idx = bisect(timestamps, dateAtMouse.getTime());
      if (idx > 0 && idx < timestamps.length) {
        const d0 = timestamps[idx - 1];
        const d1 = timestamps[idx];
        idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
      }
      idx = Math.max(0, Math.min(timestamps.length - 1, idx));

      const closestDate = allDates[idx];
      const cx = x(parsedDates[idx]);

      crosshairTop.attr('x1', cx).attr('x2', cx).style('display', null);
      crosshairBottom.attr('x1', cx).attr('x2', cx).style('display', null);

      // Find divergence value at this date
      const divEntry = safeData.divergenceIndex.find((d) => d.date === closestDate);
      const divVal = divEntry ? divEntry.value : null;
      if (divVal != null) {
        divDot.attr('cx', cx).attr('cy', yBottom(divVal)).style('display', null);
      } else {
        divDot.style('display', 'none');
      }

      // Find average path value
      const avgEntry = averagePath.find((d) => d.date === closestDate);
      const avgVal = avgEntry ? avgEntry.value : null;

      // Count paths with data at this date
      let pathCount = 0;
      safeData.paths.forEach((p) => {
        if (p.normalizedPath && p.normalizedPath.some((pt) => pt.date === closestDate)) {
          pathCount++;
        }
      });

      setTooltip({
        x: event.clientX + 14,
        y: event.clientY - 10,
        date: closestDate,
        avgValue: avgVal,
        divergence: divVal,
        pathCount,
      });
    }

    function handleLeave() {
      crosshairTop.style('display', 'none');
      crosshairBottom.style('display', 'none');
      divDot.style('display', 'none');
      setTooltip(null);
    }

    // Hover overlays
    topG.append('rect')
      .attr('width', innerW)
      .attr('height', topInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event, topG))
      .on('mouseleave', handleLeave);

    bottomG.append('rect')
      .attr('width', innerW)
      .attr('height', bottomInnerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => handleHover(event, bottomG))
      .on('mouseleave', handleLeave);

    return () => {
      svg.selectAll('*').remove();
    };
  }, [safeData, stateColorScale, averagePath]);

  // Resize handler
  useEffect(() => {
    if (!safeData) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [safeData]);

  // Empty state
  if (!safeData) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Path Divergence</div>
        <div style={styles.subtitle}>
          Normalized cumulative price trajectories and cross-address divergence
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No divergence data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Path Divergence</div>
      <div style={styles.subtitle}>
        Normalized cumulative price trajectories and cross-address divergence
        {safeData.field ? ` (${safeData.field})` : ''}
      </div>

      {/* Info box */}
      <div style={styles.infoBox}>
        {safeData.maxDivergence != null && (
          <div style={styles.infoItem}>
            <span style={{ color: '#6b7280' }}>Max Divergence:</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {safeData.maxDivergence.toFixed(4)}
            </span>
          </div>
        )}
        {safeData.spreadAtEnd != null && (
          <div style={styles.infoItem}>
            <span style={{ color: '#6b7280' }}>Spread at End:</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {safeData.spreadAtEnd.toFixed(4)}
            </span>
          </div>
        )}
        {safeData.avgFinalValue != null && (
          <div style={styles.infoItem}>
            <span style={{ color: '#6b7280' }}>Avg Final Value:</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {safeData.avgFinalValue.toFixed(4)}
            </span>
          </div>
        )}
        {safeData.mostDivergentLabel && (
          <div style={styles.infoItem}>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>Most Divergent:</span>
            <span style={{ color: '#374151' }}>{safeData.mostDivergentLabel}</span>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {/* Average path */}
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#1f2937" strokeWidth="2.5" strokeDasharray="4,3" />
          </svg>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>Average Path</span>
        </div>
        {/* Reference line */}
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="1" strokeDasharray="4,3" />
          </svg>
          <span>Baseline (1.0)</span>
        </div>
        {/* Most divergent */}
        <div style={styles.legendItem}>
          <svg width="14" height="10">
            <line x1="0" y1="5" x2="14" y2="5" stroke="#dc2626" strokeWidth="2" />
          </svg>
          <span style={{ color: '#dc2626' }}>Most Divergent</span>
        </div>
        {/* Divergence area */}
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="2" width="20" height="8" fill="#8b5cf6" fillOpacity="0.15" rx="1" />
            <line x1="0" y1="2" x2="20" y2="2" stroke="#8b5cf6" strokeWidth="2" />
          </svg>
          <span>Divergence Index</span>
        </div>
        {/* Separator */}
        <div style={{ borderLeft: '1px solid #e5e7eb', height: '16px', margin: '0 4px' }} />
        {/* State colors */}
        {uniqueStates.map((state) => {
          const color = stateColorScale(state);
          return (
            <div key={state} style={styles.legendItem}>
              <svg width="14" height="10">
                <line x1="0" y1="5" x2="14" y2="5" stroke={color} strokeWidth="2" strokeOpacity="0.6" />
              </svg>
              <span>{state}</span>
            </div>
          );
        })}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Avg Value</span>
            <span style={{ fontWeight: 600, color: '#1f2937' }}>
              {tooltip.avgValue != null ? tooltip.avgValue.toFixed(4) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#8b5cf6' }}>Divergence</span>
            <span style={{ fontWeight: 600, color: '#8b5cf6' }}>
              {tooltip.divergence != null ? tooltip.divergence.toFixed(4) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Paths</span>
            <span style={{ fontWeight: 600, color: '#6b7280' }}>
              {tooltip.pathCount}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
